import { InputActions, type Layer, ZIndexes } from "@common/constants";
import { GravityBeamMode, type GravityBeamData } from "@common/definitions/gravity";
import { HitboxType } from "@common/utils/hitbox";
import { DefinitionType } from "@common/utils/objectDefinitions";
import { Vec, type Vector } from "@common/utils/vector";
import $ from "jquery";
import { Graphics, Rectangle, Sprite, type Texture } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { CameraManager } from "../managers/cameraManager";
import { InputManager } from "../managers/inputManager";
import { MapManager } from "../managers/mapManager";
import { type Obstacle } from "../objects/obstacle";
import { type Player } from "../objects/player";
import { PIXI_SCALE } from "../utils/constants";
import { escapeText } from "../utils/misc";
import { t } from "../utils/translations/phrases";
import { translate } from "../utils/translations/translations";
import { type TranslationKeys } from "../utils/translations/typings";
import { PlasmaFx } from "./plasmaFx";

/*
 * The Gravity Gloves in the browser (what they do is in common/src/definitions/gravity.ts, the physics on the server):
 *  - the beam: a stretchy blue line from the hands to what it holds, bending and thinning as the held thing lags behind
 *    a fast swing; shooting out and flickering while it looks for something; pulling guns in
 *  - merged groups: each member encircled in blue liquid, one blob where they touch
 *  - the gloves on the hands, glowing while the beam is on
 *  - "V Merge" when the held thing touches a box or barrel it can merge with (tappable on phones, with a Launch button)
 *  - sounds, made on the spot with Web Audio: a hum while the beam is on, a thunk when it grabs, a whoosh when it lets
 *    go or launches, a bloop when two merge, a zap for a plasma burst
 *  - the plasma burst (right-click with nothing held): its bolt is a bullet (`gravity_gloves_bullet`, drawn by
 *    objects/bullet.ts), with a zap and a puff of sparks at the hands from here; "Right-click Plasma burst" shows
 *    while the gloves are out and hold nothing (a button on phones)
 * Everything is drawn from what the server sends (the beam with each player, positions and groups with each object).
 */

const BEAM_GLOW = 0x2f9bff;
const BEAM_MID = 0x5cc8ff;
const BEAM_CORE = 0xeafaff;
const LIQUID_DARK = 0x1452c2;
const LIQUID = 0x2f8dff;
const LIQUID_LIGHT = 0xa8e2ff;
/** the beam leaves the hands this far in front of the player's centre (pixels at size 1) */
const HANDS = 60;
/** how far the liquid reaches out from what it holds (pixels; a player is 45 across the middle) */
const SHELL = 18;

interface LayerRoots {
    /** under the boxes and barrels: the liquid itself */
    readonly under: Graphics
    /** over them: the liquid's sheen, the merge highlight */
    readonly over: Graphics
    /** the beams (over players, under bushes and trees) */
    readonly beams: Graphics
}

/**
 * the gloves on a player's hands: drawn in the player's container just above the fists and moved with them every
 * frame (as children of the fists they'd take on the skin's hand tint)
 */
interface Hands {
    readonly gloves: [Sprite, Sprite]
    readonly glows: [Sprite, Sprite]
}

/** a little Web Audio synth for the beam's sounds (no files to download) */
class BeamAudio {
    private _ctx?: AudioContext;
    private _hum?: { readonly osc: OscillatorNode, readonly osc2: OscillatorNode, readonly gain: GainNode, readonly filter: BiquadFilterNode };
    private _noise?: AudioBuffer;

    constructor() {
        const unlock = (): void => { void this._ensure()?.resume(); };
        window.addEventListener("pointerdown", unlock, { passive: true });
        window.addEventListener("keydown", unlock, { passive: true });
    }

    private _volume(): number {
        try {
            return Number(GameConsole.getBuiltInCVar("cv_master_volume")) * Number(GameConsole.getBuiltInCVar("cv_sfx_volume"));
        } catch { return 0.6; }
    }

    private _ensure(): AudioContext | undefined {
        try {
            if (!this._ctx) {
                const ctx = this._ctx = new AudioContext();
                const noise = this._noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
                const data = noise.getChannelData(0);
                for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
            }
            return this._ctx;
        } catch { return undefined; }
    }

    /** the beam's hum: on while it's on; higher and buzzier the harder the line is pulled */
    hum(on: boolean, tension: number): void {
        const ctx = this._ensure();
        if (!ctx || ctx.state !== "running") return;
        const volume = this._volume();
        if (on && !this._hum && volume > 0) {
            const osc = ctx.createOscillator(); osc.type = "sawtooth"; osc.frequency.value = 92;
            const osc2 = ctx.createOscillator(); osc2.type = "sine"; osc2.frequency.value = 184;
            const filter = ctx.createBiquadFilter(); filter.type = "lowpass"; filter.frequency.value = 520; filter.Q.value = 4;
            const gain = ctx.createGain(); gain.gain.value = 0.0001;
            osc.connect(filter); osc2.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
            osc.start(); osc2.start();
            gain.gain.setTargetAtTime(0.045 * volume, ctx.currentTime, 0.05);
            this._hum = { osc, osc2, gain, filter };
        }
        const hum = this._hum;
        if (!hum) return;
        if (!on) {
            hum.gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.04);
            hum.osc.stop(ctx.currentTime + 0.3);
            hum.osc2.stop(ctx.currentTime + 0.3);
            this._hum = undefined;
            return;
        }
        const t = Math.min(1, tension);
        hum.osc.frequency.setTargetAtTime(92 + 70 * t, ctx.currentTime, 0.05);
        hum.osc2.frequency.setTargetAtTime(184 + 160 * t + 6 * Math.sin(ctx.currentTime * 20), ctx.currentTime, 0.03);
        hum.filter.frequency.setTargetAtTime(520 + 1400 * t, ctx.currentTime, 0.05);
        hum.gain.gain.setTargetAtTime((0.04 + 0.03 * t) * volume, ctx.currentTime, 0.08);
    }

    /**
     * a falling tone with a click of noise: grab (low), merge (two rising blips), launch (a deep thump and a whoosh);
     * `loudness` from 0 to 1 (someone else's, farther away, is quieter). (The plasma burst's sounds are files, played
     * where it is: gravity/plasmaFx.ts)
     */
    play(kind: "grab" | "release" | "launch" | "merge", loudness = 1): void {
        const ctx = this._ensure();
        const volume = this._volume() * Math.min(1, Math.max(0, loudness));
        if (!ctx || ctx.state !== "running" || volume <= 0 || !this._noise) return;
        const now = ctx.currentTime;
        const out = ctx.createGain(); out.gain.value = volume; out.connect(ctx.destination);
        const tone = (type: OscillatorType, from: number, to: number, at: number, length: number, gain: number): void => {
            const osc = ctx.createOscillator(); osc.type = type;
            const env = ctx.createGain();
            osc.frequency.setValueAtTime(from, now + at);
            osc.frequency.exponentialRampToValueAtTime(to, now + at + length);
            env.gain.setValueAtTime(0.0001, now + at);
            env.gain.exponentialRampToValueAtTime(gain, now + at + 0.012);
            env.gain.exponentialRampToValueAtTime(0.0001, now + at + length);
            osc.connect(env); env.connect(out);
            osc.start(now + at); osc.stop(now + at + length + 0.05);
        };
        const whoosh = (from: number, to: number, length: number, gain: number): void => {
            const source = ctx.createBufferSource(); source.buffer = this._noise ?? null;
            const band = ctx.createBiquadFilter(); band.type = "bandpass"; band.Q.value = 1.6;
            band.frequency.setValueAtTime(from, now);
            band.frequency.exponentialRampToValueAtTime(to, now + length);
            const env = ctx.createGain();
            env.gain.setValueAtTime(0.0001, now);
            env.gain.exponentialRampToValueAtTime(gain, now + length * 0.3);
            env.gain.exponentialRampToValueAtTime(0.0001, now + length);
            source.connect(band); band.connect(env); env.connect(out);
            source.start(now, Math.random() * 0.5, length + 0.05);
        };
        switch (kind) {
            case "grab":
                tone("sine", 320, 85, 0, 0.16, 0.28);
                tone("square", 1400, 600, 0, 0.03, 0.05);
                break;
            case "release":
                whoosh(1800, 500, 0.22, 0.12);
                break;
            case "launch":
                tone("sine", 140, 45, 0, 0.32, 0.4);
                whoosh(600, 3200, 0.3, 0.2);
                break;
            case "merge":
                tone("sine", 420, 720, 0, 0.1, 0.22);
                tone("sine", 560, 980, 0.09, 0.14, 0.2);
                tone("triangle", 1200, 1600, 0.05, 0.08, 0.05);
                break;
        }
    }
}

class GravityFxClass {
    private readonly _beams = new Map<Player, GravityBeamData>();
    private readonly _bonds = new Map<number, Set<Obstacle>>();
    private readonly _roots = new Map<Layer, LayerRoots>();
    private readonly _hands = new Map<Player, Hands>();
    private _textures?: { readonly glove: Texture, readonly glow: Texture };
    private readonly _audio = new BeamAudio();
    private _time = 0;
    /** the local player's beam last frame (sounds, the prompt, and the wheel) */
    private _local?: GravityBeamData;
    private _promptShown = "";
    private _promptReady = false;

    /** the local player is holding something with the gloves (the wheel then moves it instead of switching weapons) */
    get localHolding(): boolean {
        return !Game.spectating && this._local?.mode === GravityBeamMode.Holding;
    }

    /** the local player has the gloves out (right-click: a launch, or a plasma burst with nothing held) */
    get glovesOut(): boolean {
        const player = Game.activePlayer;
        if (Game.spectating || !player || player.dead) return false;
        const item = player.activeItem;
        return item.defType === DefinitionType.Melee && item.gravity === true;
    }

    /**
     * a plasma burst left someone's hands (objects/bullet.ts): its flash, sparks and sound (gravity/plasmaFx.ts;
     * `heading`: the bolt's direction, from the right)
     */
    plasmaShot(position: Vector, heading: number, layer: Layer): void {
        PlasmaFx.muzzle(position, heading, layer);
    }

    /** a player's beam, as the server sends it with every update (undefined: no beam) */
    setBeam(player: Player, beam: GravityBeamData | undefined): void {
        if (beam) this._beams.set(player, beam);
        else this._beams.delete(player);
        if (player.isActivePlayer) this._localChanged(beam);
    }

    /** someone's beam holds this obstacle (or its group) right now */
    isHeld(obstacle: Obstacle): boolean {
        for (const beam of this._beams.values()) {
            if (beam.mode !== GravityBeamMode.Holding || beam.target === undefined) continue;
            if (beam.target === obstacle.id) return true;
            if (obstacle.bond && this._bonds.get(obstacle.bond)?.has(Game.objects.get(beam.target) as Obstacle)) return true;
        }
        return false;
    }

    /** an obstacle joined a merged group, left one, or went out of view */
    setBond(obstacle: Obstacle, was: number, now: number, quiet: boolean): void {
        if (was) {
            const group = this._bonds.get(was);
            group?.delete(obstacle);
            if (group?.size === 0) this._bonds.delete(was);
        }
        if (now) {
            let group = this._bonds.get(now);
            if (!group) this._bonds.set(now, group = new Set());
            const fresh = group.size === 1 && !group.has(obstacle);
            group.add(obstacle);
            // two just merged: a bloop and a splash of liquid, if it happened near us
            if (fresh && !quiet) this._merged(obstacle);
        }
    }

    /** the gloves on a player's hands (or off them) */
    dressHands(player: Player, on: boolean): void {
        let hands = this._hands.get(player);
        if (!on) {
            if (hands) {
                for (const sprite of [...hands.gloves, ...hands.glows]) sprite.destroy();
                this._hands.delete(player);
            }
            return;
        }
        if (!hands) {
            const textures = this._handTextures();
            const make = (texture: Texture, zIndex: number): Sprite => {
                const sprite = new Sprite(texture);
                sprite.anchor.set(0.5);
                sprite.zIndex = zIndex;
                return sprite;
            };
            hands = {
                gloves: [make(textures.glove, 4.05), make(textures.glove, 4.05)],
                glows: [make(textures.glow, 4.1), make(textures.glow, 4.1)]
            };
            player.container.addChild(...hands.gloves, ...hands.glows);
            this._hands.set(player, hands);
        }
        for (const sprite of [...hands.gloves, ...hands.glows]) sprite.visible = true;
        this._followHands(player, hands);
    }

    /** the gloves sit on the fists, wherever the fists are (they move with every swing and pose) */
    private _followHands(player: Player, hands: Hands): void {
        const { leftFist, rightFist } = player.images;
        [leftFist, rightFist].forEach((fist, i) => {
            for (const sprite of [hands.gloves[i], hands.glows[i]]) {
                sprite.position.copyFrom(fist.position);
                sprite.rotation = fist.rotation;
                sprite.visible = fist.visible;
            }
        });
    }

    /** a new game or view: nothing carried over */
    reset(): void {
        this._beams.clear();
        this._bonds.clear();
        this._hands.clear();
        for (const roots of this._roots.values()) {
            roots.under.destroy();
            roots.over.destroy();
            roots.beams.destroy();
        }
        this._roots.clear();
        this._audio.hum(false, 0);
        this._local = undefined;
        this._showPrompt("");
        PlasmaFx.reset();
    }

    /** every frame */
    update(delta: number): void {
        this._time += delta;
        PlasmaFx.update();
        for (const roots of this._roots.values()) {
            roots.under.clear();
            roots.over.clear();
            roots.beams.clear();
        }

        for (const [player, beam] of this._beams) {
            if (player.destroyed || player.dead) {
                this._beams.delete(player);
                continue;
            }
            this._drawBeam(player, beam);
        }

        for (const [bond, group] of this._bonds) {
            for (const member of group) if (member.destroyed || member.dead) group.delete(member);
            if (group.size === 0) {
                this._bonds.delete(bond);
                continue;
            }
            if (group.size > 1) this._drawLiquid(group);
        }

        const local = this._local;
        if (local?.mode === GravityBeamMode.Holding && local.merge !== undefined && !Game.spectating) {
            const candidate = Game.objects.get(local.merge);
            if (candidate?.isObstacle && !candidate.dead) this._drawMergeHint(candidate);
        }

        // the gloves glow, brighter while the beam is on
        for (const [player, hands] of this._hands) {
            if (player.destroyed) {
                this._hands.delete(player);
                continue;
            }
            this._followHands(player, hands);
            const active = this._beams.has(player);
            const pulse = 0.5 + 0.5 * Math.sin(this._time / (active ? 70 : 380) + player.id);
            for (const glow of hands.glows) {
                glow.alpha = active ? 0.75 + 0.25 * pulse : 0.35 + 0.25 * pulse;
                glow.scale.set(active ? 1.05 + 0.15 * pulse : 0.9 + 0.08 * pulse);
            }
        }

        // the local player's hum follows how hard the line is pulled
        if (local && !Game.spectating) {
            this._audio.hum(true, this._localTension());
        }
        this._updatePrompt();
    }

    // ───────────────────────────── drawing ─────────────────────────────

    private _rootsFor(layer: Layer): LayerRoots {
        let roots = this._roots.get(layer);
        if (!roots) {
            const make = (zIndex: number): Graphics => {
                const graphics = new Graphics();
                graphics.zIndex = zIndex;
                return graphics;
            };
            roots = {
                under: make(ZIndexes.ObstaclesLayer1 - 0.2),
                over: make(ZIndexes.ObstaclesLayer2 + 0.3),
                beams: make(ZIndexes.Players + 0.5)
            };
            this._roots.set(layer, roots);
        }
        // each layer is drawn in the container the camera shows it in (which changes going up or down stairs)
        const target = CameraManager.getContainer(layer);
        if (roots.under.parent !== target) target.addChild(roots.under, roots.over, roots.beams);
        return roots;
    }

    /** the hands, the line's end, and where the line would end if nothing lagged (all in pixels) */
    private _beamPoints(player: Player, beam: GravityBeamData): { hands: Vector, end?: Vector, rest: Vector, dir: Vector, radius: number } {
        const rotation = player.container.rotation;
        const dir = Vec(Math.cos(rotation), Math.sin(rotation));
        const center = Vec(player.container.position.x, player.container.position.y);
        const hands = Vec.add(center, Vec.scale(dir, HANDS * player.sizeMod));
        if (beam.mode === GravityBeamMode.Reaching) {
            const end = Vec.add(hands, Vec.scale(dir, beam.length * PIXI_SCALE));
            return { hands, end, rest: end, dir, radius: 0 };
        }
        const rest = Vec.add(center, Vec.scale(dir, beam.length * PIXI_SCALE));
        const target = beam.target === undefined ? undefined : Game.objects.get(beam.target);
        if (!target || target.destroyed) return { hands, rest, dir, radius: 0 };
        let radius = 30;
        if (target.isObstacle) {
            const hitbox = target.hitbox;
            radius = (hitbox.type === HitboxType.Circle
                ? hitbox.radius
                : hitbox.type === HitboxType.Rect ? Math.hypot(hitbox.max.x - hitbox.min.x, hitbox.max.y - hitbox.min.y) / 2 : 3) * PIXI_SCALE;
        }
        return { hands, end: Vec(target.container.position.x, target.container.position.y), rest, dir, radius };
    }

    private _drawBeam(player: Player, beam: GravityBeamData): void {
        const g = this._rootsFor(player.layer).beams;
        const { hands, end, rest, dir, radius } = this._beamPoints(player, beam);
        const t = this._time;
        const seed = player.id * 1.37;

        // a glow at the hands, whatever the beam is doing
        const flicker = 0.85 + 0.15 * Math.sin(t / 37 + seed);
        g.circle(hands.x, hands.y, 26 * flicker).fill({ color: BEAM_GLOW, alpha: 0.22 });
        g.circle(hands.x, hands.y, 12 * flicker).fill({ color: BEAM_CORE, alpha: 0.85 });
        if (!end) return;

        const length = Vec.len(Vec.sub(end, hands));
        if (length < 2) return;
        // how far the held thing lags behind the line's straight end: the line bends, thins and brightens
        const stretch = beam.mode === GravityBeamMode.Holding ? Vec.len(Vec.sub(end, rest)) / PIXI_SCALE : 0;
        const tension = Math.min(1, stretch / 12);
        const control = Vec.add(hands, Vec.scale(dir, length * 0.55));
        const steps = Math.max(8, Math.min(28, Math.round(length / 14)));
        const amplitude = beam.mode === GravityBeamMode.Reaching ? 7 : beam.mode === GravityBeamMode.Pulling ? 5 : 3 + 6 * tension;
        const points: Vector[] = [];
        for (let i = 0; i <= steps; i++) {
            const s = i / steps, u = 1 - s;
            const point = Vec(
                u * u * hands.x + 2 * u * s * control.x + s * s * end.x,
                u * u * hands.y + 2 * u * s * control.y + s * s * end.y
            );
            // the line's direction here, and a living wobble across it (none at the ends)
            const tangent = Vec.normalizeSafe(Vec(
                2 * u * (control.x - hands.x) + 2 * s * (end.x - control.x),
                2 * u * (control.y - hands.y) + 2 * s * (end.y - control.y)
            ));
            const wobble = Math.sin(Math.PI * s) * amplitude * (
                Math.sin(t / 45 + s * 11 + seed) * 0.7 + Math.sin(t / 23 - s * 23 + seed * 2) * 0.3
            );
            points.push(Vec.add(point, Vec(-tangent.y * wobble, tangent.x * wobble)));
        }
        const trace = (): void => {
            g.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
        };
        const thin = 1 - 0.45 * tension;
        trace();
        g.stroke({ width: 44 * thin, color: BEAM_GLOW, alpha: 0.14, cap: "round", join: "round" });
        trace();
        g.stroke({ width: 18 * thin, color: BEAM_MID, alpha: 0.45 + 0.2 * tension, cap: "round", join: "round" });
        trace();
        g.stroke({ width: 6 + 2 * tension, color: BEAM_CORE, alpha: 0.95, cap: "round", join: "round" });

        // bright pulses running out along the line (inward when pulling)
        const speed = beam.mode === GravityBeamMode.Reaching ? 1 / 220 : 1 / 420;
        for (let k = 0; k < 3; k++) {
            let s = (t * speed + k / 3 + seed) % 1;
            if (beam.mode === GravityBeamMode.Pulling) s = 1 - s;
            const at = points[Math.min(points.length - 1, Math.round(s * (points.length - 1)))];
            g.circle(at.x, at.y, 7 + 3 * tension).fill({ color: BEAM_CORE, alpha: 0.85 });
        }

        switch (beam.mode) {
            case GravityBeamMode.Reaching: {
                // the tip, searching
                const spark = 11 + 4 * Math.sin(t / 30 + seed);
                g.circle(end.x, end.y, spark * 2).fill({ color: BEAM_GLOW, alpha: 0.25 });
                g.circle(end.x, end.y, spark).fill({ color: BEAM_CORE, alpha: 0.8 });
                break;
            }
            case GravityBeamMode.Holding: {
                // a field round what's held: three arcs turning round it
                const r = radius + 16 + 4 * Math.sin(t / 120 + seed);
                const turn = t / 260 + seed;
                for (let k = 0; k < 3; k++) {
                    const a = turn + k * Math.PI * 2 / 3;
                    g.moveTo(end.x + Math.cos(a) * r, end.y + Math.sin(a) * r);
                    g.arc(end.x, end.y, r, a, a + 1.25);
                    g.stroke({ width: 7, color: BEAM_MID, alpha: 0.6 + 0.3 * tension, cap: "round" });
                }
                break;
            }
            case GravityBeamMode.Pulling: {
                g.circle(end.x, end.y, 30 + 5 * Math.sin(t / 60)).stroke({ width: 6, color: BEAM_MID, alpha: 0.65 });
                break;
            }
        }
    }

    /** the outline of an obstacle's hitbox, puffed out by `out` pixels and rippling (points in pixels) */
    private _outline(obstacle: Obstacle, out: number, ripple: number): Vector[] {
        const hitbox = obstacle.hitbox;
        const center = Vec(obstacle.container.position.x, obstacle.container.position.y);
        // where the hitbox sits relative to the obstacle's position (usually right on it)
        const shift = Vec.scale(Vec.sub(hitbox.getCenter(), obstacle.position), PIXI_SCALE);
        const middle = Vec.add(center, shift);
        const t = this._time;
        const seed = obstacle.id * 0.731;
        const wave = (a: number): number => ripple * (Math.sin(a * 3 + t / 420 + seed) * 0.6 + Math.sin(a * 5 - t / 310 + seed * 1.7) * 0.4);
        const points: Vector[] = [];
        if (hitbox.type === HitboxType.Circle) {
            const r = hitbox.radius * PIXI_SCALE + out;
            for (let i = 0; i < 32; i++) {
                const a = i / 32 * Math.PI * 2;
                const rr = r + wave(a);
                points.push(Vec(middle.x + Math.cos(a) * rr, middle.y + Math.sin(a) * rr));
            }
            return points;
        }
        // a rectangle with rounded corners (the corners' radius is how far it's puffed out)
        const half = hitbox.type === HitboxType.Rect
            ? Vec((hitbox.max.x - hitbox.min.x) * PIXI_SCALE / 2, (hitbox.max.y - hitbox.min.y) * PIXI_SCALE / 2)
            : Vec(40, 40);
        const corners = [Vec(half.x, half.y), Vec(-half.x, half.y), Vec(-half.x, -half.y), Vec(half.x, -half.y)];
        for (let c = 0; c < 4; c++) {
            const corner = corners[c];
            const start = c * Math.PI / 2;
            for (let i = 0; i <= 6; i++) {
                const a = start + i / 6 * Math.PI / 2;
                const rr = out + wave(a + c);
                points.push(Vec(middle.x + corner.x + Math.cos(a) * rr, middle.y + corner.y + Math.sin(a) * rr));
            }
        }
        return points;
    }

    /** a merged group: each member encircled in blue liquid, run together where they touch */
    private _drawLiquid(group: Set<Obstacle>): void {
        let layer: Layer | undefined;
        for (const member of group) { layer = member.layer; break; }
        if (layer === undefined) return;
        const { under, over } = this._rootsFor(layer);
        // all the dark first, then all the bright: overlapping shells read as one blob
        for (const member of group) under.poly(this._outline(member, SHELL + 3, 2.6)).fill({ color: LIQUID_DARK });
        for (const member of group) under.poly(this._outline(member, SHELL - 4, 1.8)).fill({ color: LIQUID });
        for (const member of group) {
            // the sheen: a light rim, and a blue tint over the object as if it sat in the liquid
            over.poly(this._outline(member, SHELL - 1, 2.6)).stroke({ width: 4, color: LIQUID_LIGHT, alpha: 0.55 });
            over.poly(this._outline(member, -2, 0)).fill({ color: LIQUID, alpha: 0.16 });
            // a few bubbles drifting round in it
            const center = Vec(member.container.position.x, member.container.position.y);
            const hitbox = member.hitbox;
            const reach = (hitbox.type === HitboxType.Circle
                ? hitbox.radius
                : hitbox.type === HitboxType.Rect ? Math.max(hitbox.max.x - hitbox.min.x, hitbox.max.y - hitbox.min.y) / 2 : 3) * PIXI_SCALE + SHELL * 0.55;
            for (let k = 0; k < 3; k++) {
                const a = this._time / (900 + k * 270) + k * 2.1 + member.id;
                over.circle(center.x + Math.cos(a) * reach, center.y + Math.sin(a) * reach, 3.5 + k).fill({ color: LIQUID_LIGHT, alpha: 0.6 });
            }
        }
    }

    /** what V would merge with: a pulsing ring of liquid round it */
    private _drawMergeHint(obstacle: Obstacle): void {
        const { over } = this._rootsFor(obstacle.layer);
        const pulse = 0.5 + 0.5 * Math.sin(this._time / 110);
        over.poly(this._outline(obstacle, SHELL * (0.6 + 0.4 * pulse), 2)).stroke({ width: 6 + 4 * pulse, color: LIQUID_LIGHT, alpha: 0.5 + 0.4 * pulse });
    }

    private _handTextures(): { readonly glove: Texture, readonly glow: Texture } {
        if (this._textures) return this._textures;
        const bake = (draw: (g: Graphics) => void): Texture => {
            const g = new Graphics();
            draw(g);
            const b = g.getLocalBounds();
            const frame = new Rectangle(Math.floor(b.minX) - 1, Math.floor(b.minY) - 1, Math.ceil(b.maxX - b.minX) + 3, Math.ceil(b.maxY - b.minY) + 3);
            const texture = Game.pixi.renderer.generateTexture({ target: g, frame, resolution: 3, antialias: true });
            g.destroy();
            return texture;
        };
        // a dark armoured glove over the hand (it's drawn centred on the fist), with an emitter plate on the knuckles
        const glove = bake(g => g
            .circle(0, 0, 16).fill({ color: 0x1b2638 }).stroke({ width: 2.5, color: 0x0a0f18 })
            .moveTo(-9, -12).arc(0, 0, 15, Math.PI * 1.15, Math.PI * 0.85, true).stroke({ width: 4, color: 0x3a4f72, alpha: 0.9 })
            .roundRect(2, -8, 10, 16, 4).fill({ color: 0x2c3c58 }).stroke({ width: 1.5, color: 0x0a0f18 }));
        // the emitter's light (scaled and faded every frame)
        const glow = bake(g => g
            .circle(7, 0, 11).fill({ color: BEAM_GLOW, alpha: 0.35 })
            .circle(7, 0, 6).fill({ color: 0x9fe6ff })
            .circle(7, 0, 3).fill({ color: 0xffffff }));
        return this._textures = { glove, glow };
    }

    // ───────────────────────────── the local player ─────────────────────────────

    private _localTension(): number {
        const player = Game.activePlayer;
        const beam = this._local;
        if (!player || !beam || beam.mode !== GravityBeamMode.Holding) return beam ? 0.15 : 0;
        const { end, rest } = this._beamPoints(player, beam);
        return end ? Math.min(1, Vec.len(Vec.sub(end, rest)) / PIXI_SCALE / 12) : 0;
    }

    private _localChanged(beam: GravityBeamData | undefined): void {
        const before = this._local;
        this._local = beam;
        if (Game.spectating && !beam) {
            this._audio.hum(false, 0);
            return;
        }
        const was = before?.mode ?? GravityBeamMode.Off;
        const now = beam?.mode ?? GravityBeamMode.Off;
        if (was === now) return;
        if (now === GravityBeamMode.Holding || now === GravityBeamMode.Pulling) this._audio.play("grab");
        if (now === GravityBeamMode.Off) {
            this._audio.hum(false, 0);
            if (was === GravityBeamMode.Holding && before?.target !== undefined) {
                // let go: did it fly off fast (launched), or just drop? (its new speed arrives with the next update)
                const id = before.target;
                Game.addTimeout(() => {
                    const target = Game.objects.get(id);
                    const fast = target?.isObstacle === true && target.speed > 0.07;
                    this._audio.play(fast ? "launch" : "release");
                }, 70);
            }
        }
    }

    private _merged(obstacle: Obstacle): void {
        const player = Game.activePlayer;
        if (!player || Vec.len(Vec.sub(player.position, obstacle.position)) > 60) return;
        this._audio.play("merge");
    }

    // ───────────────────────────── the prompt ─────────────────────────────

    private _initPrompt(): void {
        if (this._promptReady) return;
        this._promptReady = true;
        const mobile = InputManager.isMobile;
        $("#gravity-prompt").toggleClass("mobile", mobile);
        $("#gravity-merge").on("pointerdown", event => {
            if (!mobile) return;
            event.preventDefault();
            InputManager.addAction(InputActions.GravityMerge);
        });
        $("#gravity-launch").on("pointerdown", event => {
            if (!mobile) return;
            event.preventDefault();
            InputManager.addAction(InputActions.GravityLaunch);
        });
        // (the server fires a plasma burst for it when nothing is held)
        $("#gravity-plasma").on("pointerdown", event => {
            if (!mobile) return;
            event.preventDefault();
            InputManager.addAction(InputActions.GravityLaunch);
        });
    }

    /**
     * "V Merge" when the held thing touches something it can merge with; while holding, how to push, pull and launch;
     * with the gloves out and nothing held, how to fire a plasma burst
     */
    private _updatePrompt(): void {
        const local = this._local;
        let state = "";
        // (not over the full-screen map, where it floated above the map and took taps on phones)
        if (!Game.spectating && !Game.gameOver && !MapManager.expanded) {
            if (local?.mode === GravityBeamMode.Holding) state = local.merge !== undefined ? "merge" : "hold";
            else if (local?.mode !== GravityBeamMode.Pulling && this.glovesOut) state = "plasma";
        }
        this._showPrompt(state);
    }

    private _keyLabel(action: string, _fallback: string): string {
        const bind = InputManager.binds.getInputsBoundToAction(action)[0];
        // nothing bound (binding another action to its key unbinds it): say so, rather than show a key that does nothing
        if (!bind) return translate("none" as TranslationKeys);
        const icon = InputManager.getIconFromInputName(bind);
        return icon ? `<img src="${icon}" alt="${bind}"/>` : bind;
    }

    private _showPrompt(state: string): void {
        if (state === this._promptShown) return;
        this._promptShown = state;
        const prompt = $("#gravity-prompt");
        if (!prompt.length) return;
        this._initPrompt();
        if (!state) {
            prompt.hide();
            return;
        }
        const mobile = InputManager.isMobile;
        const tap = `<img src="./img/misc/tap-icon.svg" alt="${escapeText(t("Tap"))}">`;
        $("#gravity-merge-key").html(mobile ? tap : this._keyLabel("gravity_merge", "V"));
        $("#gravity-merge-text").text(translate("gravity_merge" as TranslationKeys));
        $("#gravity-launch-key").html(mobile ? tap : this._keyLabel("gravity_launch", "Mouse2"));
        $("#gravity-launch-text").text(translate("gravity_launch" as TranslationKeys));
        $("#gravity-plasma-key").html(mobile ? tap : this._keyLabel("gravity_launch", "Mouse2"));
        $("#gravity-plasma-text").text(translate("gravity_plasma" as TranslationKeys));
        const plasma = state === "plasma";
        $("#gravity-wheel").toggle(!mobile && !plasma).find("span").text(translate("gravity_wheel" as TranslationKeys));
        $("#gravity-wheel-key").html(this._keyLabel("cycle_items -1", "MWheelUp"));
        $("#gravity-launch").toggle(!plasma);
        $("#gravity-plasma").toggle(plasma);
        $("#gravity-merge").toggle(state === "merge");
        prompt.toggleClass("can-merge", state === "merge").toggleClass("plasma", plasma).show();
    }
}

export const GravityFx = new GravityFxClass();
